// FinKloz — Bank Rec Upload Zone
// Drag-drop area for uploading bank statements or GL exports.

import React, { useState, useRef, useCallback } from 'react';
import { sans, mono, green, greenLight, amber, amberLight, useTheme } from '../tokens';

const teal = '#0D9488';
const tealLight = '#CCFBF1';

export default function BankRecUploadZone({ label, acceptedFormats, onFileProcessed, parsedResult, fileName, error: externalError }) {
  const t = useTheme();
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = useCallback(async (file) => {
    setError(null);
    setProcessing(true);
    try {
      await onFileProcessed(file);
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  }, [onFileProcessed]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files?.[0]) handleFile(files[0]);
  }, [handleFile]);

  const displayError = externalError || error;

  // Already has a file — show compact result
  if (parsedResult && fileName) {
    return (
      <div style={{
        padding: '10px 14px',
        background: t.bgSurface,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
      }}>
        <div>
          <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 600, color: t.textPrimary }}>{label}</div>
          <div style={{ fontFamily: mono, fontSize: 11, color: t.textPrimary, marginTop: 2 }}>{fileName}</div>
          {parsedResult.count !== undefined && (
            <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginTop: 2 }}>
              {parsedResult.count} transactions parsed
            </div>
          )}
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          style={{
            fontFamily: sans, fontSize: 11, padding: '4px 10px',
            border: `1px solid ${t.border}`, borderRadius: 4,
            background: t.bg, color: t.textSecondary, cursor: 'pointer',
          }}
        >
          Re-upload
        </button>
        <input
          ref={fileRef} type="file" style={{ display: 'none' }}
          accept={acceptedFormats || '.pdf,.csv,.xls,.xlsx,.xlsm'}
          onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
      </div>
    );
  }

  // Upload zone
  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => !processing && fileRef.current?.click()}
        style={{
          padding: '20px 16px',
          border: `2px dashed ${dragOver ? teal : t.border}`,
          borderRadius: 8,
          background: dragOver ? tealLight : t.bgSurface,
          textAlign: 'center',
          cursor: processing ? 'wait' : 'pointer',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ fontFamily: sans, fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
          {processing ? 'Processing...' : label}
        </div>
        <div style={{ fontFamily: sans, fontSize: 11, color: t.textSecondary, marginTop: 4 }}>
          {processing ? 'Parsing file...' : 'Drop file here or click to browse (PDF, CSV, XLS, XLSX)'}
        </div>
      </div>

      {displayError && (
        <div style={{
          fontFamily: sans, fontSize: 11, color: '#C0392B',
          marginTop: 6, padding: '6px 10px',
          background: '#FCEBEB', borderRadius: 4,
        }}>
          {displayError}
        </div>
      )}

      <input
        ref={fileRef} type="file" style={{ display: 'none' }}
        accept={acceptedFormats || '.pdf,.csv,.xls,.xlsx,.xlsm'}
        onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
      />
    </div>
  );
}
